import { useState, useCallback, useEffect, useMemo } from 'react';
import { api } from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { useStreaming } from '@/lib/hooks/useStreaming';

const WEBSOCKET_ENDPOINT = import.meta.env.VITE_WEBSOCKET_ENDPOINT || '';
const MAX_CONVERSATION_MESSAGES = 20;

export interface FiasAIMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  searchResults?: Array<{
    archId: string;
    name: string;
    simpleDescription: string;
    imageRef: string | null;
    fitnessScore: number;
    relevance?: number;
  }>;
  recommendedArches?: Array<{
    archId: string;
    name: string;
    simpleDescription: string;
    imageRef: string | null;
  }>;
  modelInfo?: {
    provider: string;
    modelName: string;
    displayName: string;
    tier: string;
  } | null;
}

interface UseFiasAIReturn {
  mode: { searchEnabled: boolean; aiEnabled: boolean };
  setSearchEnabled: (v: boolean) => void;
  setAIEnabled: (v: boolean) => void;
  messages: FiasAIMessage[];
  setMessages: React.Dispatch<React.SetStateAction<FiasAIMessage[]>>;
  submit: (query: string) => Promise<void>;
  stop: () => void;
  reset: () => void;
  isStreaming: boolean;
  isSearching: boolean;
  currentModel: {
    provider: string;
    modelName: string;
    displayName: string;
    tier: string;
  } | null;
  /** Latest search results for sidebar sync */
  latestSearchResults: FiasAIMessage['searchResults'];
  latestSearchQuery: string | null;
}

export function useFiasAI(): UseFiasAIReturn {
  const [searchEnabled, setSearchEnabled] = useState(true);
  const [aiEnabled, setAIEnabled] = useState(true);
  const [messages, setMessages] = useState<FiasAIMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [currentModel, setCurrentModel] = useState<UseFiasAIReturn['currentModel']>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [latestSearchResults, setLatestSearchResults] =
    useState<FiasAIMessage['searchResults']>(undefined);

  // Streaming state
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [currentAssistantMessageId, setCurrentAssistantMessageId] = useState<string | null>(null);
  const authToken = authApi.getIdToken();

  // Track latest search query for sidebar context
  const [latestSearchQuery, setLatestSearchQuery] = useState<string | null>(null);

  // Handle search results from WebSocket (backend-driven search)
  const handleSearchResults = useCallback((results: unknown[], query: string) => {
    const mapped = (
      results as Array<{
        archId: string;
        name: string;
        simpleDescription: string;
        imageRef: string | null;
        fitnessScore: number;
        relevance?: number;
      }>
    ).filter((a) => a.archId !== 'arche_make_a_request');
    setLatestSearchResults(mapped.length > 0 ? mapped : undefined);
    setLatestSearchQuery(query);
  }, []);

  // WebSocket streaming hook
  const {
    streamingText,
    isComplete: streamingComplete,
    completePayload,
    error: streamingError,
    reset: resetStreaming,
  } = useStreaming({
    requestId: currentRequestId,
    authToken: authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!currentRequestId,
    onSearchResults: handleSearchResults,
  });

  // Combine streaming text from all nodes
  const combinedStreamingText = useMemo(() => {
    return Object.values(streamingText).join('');
  }, [streamingText]);

  // Update assistant message with streaming text
  useEffect(() => {
    if (currentAssistantMessageId && combinedStreamingText) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId ? { ...msg, content: combinedStreamingText } : msg,
        ),
      );
    }
  }, [currentAssistantMessageId, combinedStreamingText]);

  // Handle streaming completion
  useEffect(() => {
    if (streamingComplete && currentAssistantMessageId) {
      // Extract AI-driven recommendations from the complete event payload
      const payloadArches = completePayload?.recommendedArches as
        | FiasAIMessage['recommendedArches']
        | undefined;

      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id !== currentAssistantMessageId) return msg;

          return {
            ...msg,
            isStreaming: false,
            ...(payloadArches && payloadArches.length > 0
              ? { recommendedArches: payloadArches }
              : {}),
          };
        }),
      );

      // Update sidebar with search results from complete payload
      const payloadSearchResults = completePayload?.searchResults as
        | Array<{
            archId: string;
            name: string;
            simpleDescription: string;
            imageRef: string | null;
            fitnessScore: number;
            relevance?: number;
          }>
        | undefined;

      if (payloadSearchResults && payloadSearchResults.length > 0) {
        // Reorder: put AI-recommended arches first
        if (payloadArches && payloadArches.length > 0) {
          const recommendedIds = new Set(payloadArches.map((a) => a.archId));
          const promoted = payloadSearchResults.filter((r) => recommendedIds.has(r.archId));
          const rest = payloadSearchResults.filter((r) => !recommendedIds.has(r.archId));
          promoted.sort((a, b) => {
            const aIdx = payloadArches.findIndex((p) => p.archId === a.archId);
            const bIdx = payloadArches.findIndex((p) => p.archId === b.archId);
            return aIdx - bIdx;
          });
          setLatestSearchResults([...promoted, ...rest]);
        } else {
          setLatestSearchResults(payloadSearchResults);
        }
      }

      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      resetStreaming();
    }
  }, [streamingComplete, currentAssistantMessageId, resetStreaming, completePayload]);

  // Handle streaming errors
  useEffect(() => {
    if (streamingError && currentAssistantMessageId) {
      const isAuthError =
        streamingError.message.toLowerCase().includes('unauthorized') ||
        streamingError.code === 'UNAUTHORIZED';

      const errorMessage = isAuthError
        ? 'Your session has expired. Please log out and log back in to continue.'
        : `Error: ${streamingError.message}`;

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId
            ? { ...msg, content: errorMessage, isStreaming: false }
            : msg,
        ),
      );
      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      resetStreaming();
    }
  }, [streamingError, currentAssistantMessageId, resetStreaming]);

  const submit = useCallback(
    async (query: string) => {
      const trimmedQuery = query.trim();
      if (!trimmedQuery || isStreaming || isSearching) return;

      const userMessageId = `user-${Date.now()}`;
      const assistantMessageId = `assistant-${Date.now()}`;

      // Add user message
      const userMessage: FiasAIMessage = {
        id: userMessageId,
        role: 'user',
        content: trimmedQuery,
      };

      // Determine mode
      const mode = searchEnabled && aiEnabled ? 'hybrid' : aiEnabled ? 'ai-only' : 'search-only';

      if (mode === 'search-only') {
        // Search-only mode: just run marketplace search
        setMessages((prev) => [...prev, userMessage]);
        setIsSearching(true);

        try {
          const result = await api.searchMarketplace({ q: trimmedQuery, limit: 10 });
          const mapped = result.arches
            .filter((a) => a.archId !== 'arche_make_a_request')
            .map((a) => ({
              archId: a.archId,
              name: a.name,
              simpleDescription: a.simpleDescription,
              imageRef: a.imageRef,
              fitnessScore: a.fitnessScore,
              relevance: a.relevance,
            }));

          setLatestSearchResults(mapped.length > 0 ? mapped : undefined);

          const assistantMessage: FiasAIMessage = {
            id: assistantMessageId,
            role: 'assistant',
            content:
              mapped.length > 0
                ? 'Here are the top matches for your Re-Quest:'
                : 'No arches found matching your request. Try a different search or browse the Arche Store.',
            searchResults: mapped.length > 0 ? mapped : undefined,
          };

          setMessages((prev) => [...prev, assistantMessage]);
        } catch (err) {
          console.error('Search failed:', err);
          setMessages((prev) => [
            ...prev,
            {
              id: assistantMessageId,
              role: 'assistant',
              content: 'Search failed. Please try again.',
            },
          ]);
        } finally {
          setIsSearching(false);
        }
        return;
      }

      // AI mode (ai-only or hybrid): stream AI response
      const assistantMessage: FiasAIMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        isStreaming: true,
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setIsStreaming(true);

      // Build conversation history
      const allMessages = [...messages, userMessage].filter((msg) => msg.content.trim().length > 0);
      const truncatedMessages = allMessages.slice(-MAX_CONVERSATION_MESSAGES);
      const conversationHistory = truncatedMessages
        .filter((m) => m.role === 'user' || (m.role === 'assistant' && !m.isStreaming))
        .map((msg) => ({
          role: msg.role as 'user' | 'assistant',
          content: msg.content,
        }));

      try {
        // Start AI streaming
        const result = await api.fiasAIChat({
          message: trimmedQuery,
          conversationId: conversationId || undefined,
          mode,
          routingPreference: 'auto',
          conversationHistory,
        });

        setConversationId(result.conversationId);
        setCurrentModel(result.selectedModel);
        setCurrentRequestId(result.requestId);
        setCurrentAssistantMessageId(assistantMessageId);

        // Update assistant message with model info
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId ? { ...msg, modelInfo: result.selectedModel } : msg,
          ),
        );
      } catch (err) {
        console.error('Fias AI chat failed:', err);

        const errorStr = String(err).toLowerCase();
        const isAuthError = errorStr.includes('unauthorized') || errorStr.includes('token');

        const errorMessage = isAuthError
          ? 'Your session has expired. Please log out and log back in to continue.'
          : 'Sorry, something went wrong. Please try again.';

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? { ...msg, content: errorMessage, isStreaming: false }
              : msg,
          ),
        );
        setIsStreaming(false);
      }
    },
    [searchEnabled, aiEnabled, messages, isStreaming, isSearching, conversationId],
  );

  const stop = useCallback(() => {
    if (!isStreaming || !currentAssistantMessageId) return;

    // Finalize the current message with whatever content has streamed so far
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === currentAssistantMessageId ? { ...msg, isStreaming: false } : msg,
      ),
    );
    setIsStreaming(false);
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    resetStreaming();
  }, [isStreaming, currentAssistantMessageId, resetStreaming]);

  const reset = useCallback(() => {
    setMessages([]);
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    setConversationId(null);
    setCurrentModel(null);
    setIsStreaming(false);
    setIsSearching(false);
    setLatestSearchResults(undefined);
    setLatestSearchQuery(null);
    resetStreaming();
  }, [resetStreaming]);

  return {
    mode: { searchEnabled, aiEnabled },
    setSearchEnabled,
    setAIEnabled,
    messages,
    setMessages,
    submit,
    stop,
    reset,
    isStreaming,
    isSearching,
    currentModel,
    latestSearchResults,
    latestSearchQuery,
  };
}
